import { useState, useEffect, useCallback } from "react";
import { adminLogin, adminLogout, adminSession, fetchLeads, fetchStats, updateLeadStatus, deleteLead, bulkDeleteLeads, addLeadNote, setLeadFollowUp, sendLeadReply } from "../lib/api";

// Auth state: null = checking the HttpOnly cookie session, true/false = known.
export const useAdminSession = () => {
  const [authed, setAuthed] = useState(null);
  const [email, setEmail] = useState("");

  useEffect(() => {
    adminSession().then((s) => { setAuthed(!!s.authenticated); setEmail(s.email || ""); }).catch(() => setAuthed(false));
  }, []);

  const login = async (loginEmail, password) => {
    await adminLogin(loginEmail, password);
    setEmail(loginEmail.toLowerCase());
    setAuthed(true);
  };

  const logout = async () => {
    try {
      await adminLogout();
    } finally {
      setAuthed(false);
    }
  };

  return { authed, setAuthed, email, login, logout };
};

export const useAdminLeads = () => {
  const session = useAdminSession();
  const { authed, setAuthed } = session;
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const [l, s] = await Promise.all([fetchLeads(), fetchStats()]);
      setLeads(l);
      setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) setAuthed(false);
    } finally {
      setLoading(false);
    }
  }, [authed, setAuthed]);

  useEffect(() => {
    load();
  }, [load]);

  const applyLead = (updated) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    load();
    return updated;
  };

  const setStatus = async (id, status) => applyLead(await updateLeadStatus(id, status));
  const addNote = async (id, text, followUpAt) => applyLead(await addLeadNote(id, text, followUpAt));
  const setFollowUp = async (id, followUpAt) => applyLead(await setLeadFollowUp(id, followUpAt));
  const sendReply = async (id, templateId, note) => applyLead(await sendLeadReply(id, templateId, note));
  const removeLead = async (id) => {
    await deleteLead(id);
    setLeads((prev) => prev.filter((l) => l.id !== id));
    load();
  };

  const removeLeads = async (ids) => {
    const r = await bulkDeleteLeads(ids);
    setLeads((prev) => prev.filter((l) => !ids.includes(l.id)));
    load();
    return r.deleted;
  };

  return { ...session, leads, stats, loading, load, setStatus, addNote, setFollowUp, sendReply, removeLead, removeLeads };
};
